// Media library: every uploaded file, which episodes use it, deleting
// unused files, and turning a file into a new episode in one step.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { MediaFile, ShowEpisode } from './shows';

export interface LibraryFile extends MediaFile {
  mediaType?: 'audio' | 'video';
  usedBy: Array<{ id: string; podcastId: string; title: string; status: string }>;
}

export function useMediaLibrary() {
  return useQuery<{ media: LibraryFile[]; total: number }>({
    queryKey: ['media-list'],
    queryFn: () => api('/api/media?limit=100'),
  });
}

export function useDeleteMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/api/media/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['media-list'] }),
  });
}

/** Create a draft episode in a show with this file attached. */
export function useEpisodeFromMedia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ podcastId, file }: { podcastId: string; file: LibraryFile }) => {
      const { episode } = await api<{ episode: ShowEpisode }>('/api/episodes', {
        method: 'POST',
        body: JSON.stringify({ podcastId, title: titleFromFilename(file.originalName) }),
      });
      await api(`/api/episodes/${episode.id}`, { method: 'PATCH', body: JSON.stringify({ mediaFileId: file.id }) });
      return episode;
    },
    onSuccess: (episode) => {
      void qc.invalidateQueries({ queryKey: ['media-list'] });
      void qc.invalidateQueries({ queryKey: ['episodes', episode.podcastId] });
    },
  });
}

/** "ep-12_final_mix.wav" → "Ep 12 final mix" */
export function titleFromFilename(name: string | null): string {
  const base = (name ?? '').replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Untitled episode';
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v >= 10 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}
